import { useEffect, useState } from 'react';
import api from '@/lib/api';
import ProductCard from '@/components/shop/ProductCard';

/**
 * Reusable recommendations strip. Reuses the existing ProductCard component.
 *
 * Props:
 *  - productId: string     -> uses /products/:id/recommendations
 *  - cartIds:  string[]    -> uses /recommendations/cart
 *  - title:    string      -> section heading (default: "You may also like")
 *  - limit:    number      -> default 4
 *  - compact:  boolean     -> tighter grid for narrow surfaces (cart drawer)
 */
export default function Recommendations({
  productId,
  cartIds,
  title = 'You may also like',
  limit = 4,
  compact = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const req = productId
      ? api.get(`/products/${productId}/recommendations`, { params: { limit } })
      : api.post('/recommendations/cart', { product_ids: cartIds || [] }, { params: { limit } });
    req
      .then((r) => { if (!cancelled) setItems(r.data || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productId, cartIds && cartIds.join(','), limit]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!loading && items.length === 0) return null;

  const gridCls = compact
    ? 'grid grid-cols-2 gap-3'
    : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4';

  return (
    <section data-testid="recommendations-section" className="mt-10 sm:mt-14">
      <h2 className="font-heading text-xl sm:text-2xl text-stone-900 mb-4">{title}</h2>
      <div className={gridCls}>
        {(loading ? Array.from({ length: limit }) : items).map((p, idx) => (
          p ? (
            <ProductCard key={p.id} product={p} />
          ) : (
            <div key={idx} className="aspect-[3/4] rounded-3xl bg-stone-100 animate-pulse" />
          )
        ))}
      </div>
    </section>
  );
}
